// Themenwelten (Querschnitt über Orte und Beiträge); ohne Node-Abhängigkeiten (auch im Browser nutzbar).
import { PLACE_TYPES, type PlaceType } from "@/lib/travel-shared";

/** Ausgangskatalog, solange eine Themenwelt ihre Kategorien noch nicht selbst gespeichert hat. */
export const THEME_CATEGORIES = {
  hotels: { label: "Hotels", intro: "Unterkünfte", types: ["hotel"] },
  "bars-clubs": { label: "Bars & Clubs", intro: "Bars und Clubs", types: ["bar", "club"] },
  restaurants: { label: "Restaurants", intro: "Restaurants und Cafés", types: ["restaurant"] },
  strand: { label: "Strand", intro: "Strände und Strandabschnitte", types: ["beach"] },
  wellness: { label: "Wellness", intro: "Wellness und Sauna", types: ["wellness"] },
} as const satisfies Record<string, { label: string; intro: string; types: readonly PlaceType[] }>;
export type ThemeCategory = keyof typeof THEME_CATEGORIES;
export const THEME_CATEGORY_KEYS = Object.keys(THEME_CATEGORIES) as [ThemeCategory, ...ThemeCategory[]];

export type ThemeCategoryDef = { slug: string; label: string; intro: string; types: PlaceType[] };

const isPlaceType = (t: string): t is PlaceType => t in PLACE_TYPES;

const defaultCategories = (): ThemeCategoryDef[] => THEME_CATEGORY_KEYS.map((slug) => ({
  slug, label: THEME_CATEGORIES[slug].label, intro: THEME_CATEGORIES[slug].intro, types: [...THEME_CATEGORIES[slug].types],
}));

function parseItem(item: unknown): ThemeCategoryDef | null {
  if (!item || typeof item !== "object") return null;
  const row = item as { slug?: unknown; label?: unknown; intro?: unknown; types?: unknown };
  const slug = typeof row.slug === "string" ? row.slug : "";
  const label = typeof row.label === "string" ? row.label.trim().slice(0, 80) : "";
  if (!/^[a-z0-9-]{1,80}$/.test(slug) || !label) return null;
  const intro = typeof row.intro === "string" ? row.intro.trim().slice(0, 200) : "";
  const types = Array.isArray(row.types) ? [...new Set(row.types.filter((t): t is PlaceType => typeof t === "string" && isPlaceType(t)))] : [];
  return types.length ? { slug, label, intro, types } : null;
}

/** Gespeicherte Kategorien. Alte Listen aus Schlüssel-Texten bleiben vollständig; eine eigene Liste gilt genau so, auch leer. */
export function themeCategories(raw: string | null | undefined): ThemeCategoryDef[] {
  let list: unknown = [];
  try {
    list = JSON.parse(raw || "[]");
  } catch {
    list = [];
  }
  if (list && typeof list === "object" && !Array.isArray(list) && (list as { custom?: unknown }).custom === true) {
    const items = (list as { items?: unknown }).items;
    const rows = Array.isArray(items) ? items.map(parseItem).filter((x): x is ThemeCategoryDef => !!x) : [];
    const seen = new Set<string>();
    return rows.filter((x) => (seen.has(x.slug) ? false : (seen.add(x.slug), true)));
  }
  if (!Array.isArray(list)) return defaultCategories();
  if (list.every((x) => typeof x === "string")) {
    const keys = list.filter((k): k is ThemeCategory => typeof k === "string" && k in THEME_CATEGORIES);
    return [...new Set([...keys, ...THEME_CATEGORY_KEYS])].map((slug) => ({
      slug, label: THEME_CATEGORIES[slug].label, intro: THEME_CATEGORIES[slug].intro, types: [...THEME_CATEGORIES[slug].types],
    }));
  }
  const rows = list.map(parseItem).filter((x): x is ThemeCategoryDef => !!x);
  return rows.length ? rows : defaultCategories();
}

export const categoryOrder = (raw: string | null | undefined) => themeCategories(raw).map((c) => c.slug);

/** Adressteil aus einem Namen: Kleinbuchstaben, Bindestriche. */
export function themeSlug(name: string) {
  const s = name.trim().toLowerCase().replace(/ß/g, "ss").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
  return s || "kategorie";
}

/** Themenwelten mit eigener Hub-Seite /<slug> (je ein Ordner unter src/app). */
export const THEME_HUBS = ["lgbt"] as const;
export const hasHub = (slug: string) => (THEME_HUBS as readonly string[]).includes(slug);

/**
 * Themenwelten, deren Aufrufe besonders geschützt werden (besondere Kategorie personenbezogener Daten):
 * Statistik nur nach Einwilligung und ohne Besucherkennung, keine Suchbegriffe.
 */
export const SENSITIVE_THEMES = new Set(["lgbt"]);
export const SENSITIVE_PATH = /^\/lgbt(\/|$)/;

/** Suchbegriffe mit Bezug zur sexuellen Orientierung oder Identität werden nie gespeichert. */
const SENSITIVE_TERMS = /(lgbt|lgbtq|queer|gay|schwul|lesb|bisex|trans|homo|pride|csd|drag|men[\s-]?only|gay[\s-]?friendly|nudist|fkk)/i;
export const isSensitiveSearch = (q: string) => SENSITIVE_TERMS.test(q);
